'use client'

import Image from '@dustin-web/microsite-components/src/image'
import {
  getProductUrl,
  getRelativeProductImageUrl,
} from '@dustin-web/microsite-components/src/utils/urls'
import React, { useEffect, useState } from 'react'
import { ProductVariantValue } from './product-variants'
import Link from 'next/link'
import classNames from 'classnames'
import { ProductForPageFragment } from '@productsite-beta/src/graphql/generated/graphql-types'
import { hasNoValidVariantCombinations, isNotAvailableForSale } from './product-variant-helper'
import { ProductDigitalData } from '@dustin-web/product-components/src/utils/product-digital-data'
import router from 'next/router'

type Props = {
  variantName: string
  variantNameEng?: string | null
  variantValues: ProductVariantValue[]
  mainProduct: ProductForPageFragment
  onClick: (arg0: string[], arg1: string) => void
}

const DONT_SHOW_AGAIN_LOCALSTORAGE_KEY = 'dustin.modal.variants'

const ProductVariantImageCards = ({
  variantName,
  variantNameEng,
  variantValues,
  mainProduct,
  onClick,
}: Props) => {
  const mainProductErpIdentifier = mainProduct.productErpIdentifier
  const ownVariant = variantValues.find(x => x?.productErpIdentifier === mainProductErpIdentifier)
  const ownVariantValueText = ownVariant?.itemValueText
  const [selectedColor, setSelectedColor] = useState<string | undefined | null>(ownVariantValueText)
  function handleOnUnavailableClicked(productVariantValue: any) {
    if (localStorage.getItem(DONT_SHOW_AGAIN_LOCALSTORAGE_KEY) !== 'true') {
      onClick(
        productVariantValue.missingVariantNames,
        getProductUrl(
          productVariantValue.productErpIdentifier || '',
          productVariantValue.product?.nameSlug || ''
        )
      )
    } else {
      router.push(
        getProductUrl(
          productVariantValue.productErpIdentifier || '',
          productVariantValue.product?.nameSlug || ''
        )
      )
    }
  }

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handlePageHide = () => {
      localStorage.removeItem(DONT_SHOW_AGAIN_LOCALSTORAGE_KEY)
    }
    window.addEventListener('pagehide', handlePageHide)
    return () => window.removeEventListener('pagehide', handlePageHide)
  }, [])

  useEffect(() => {
    setSelectedColor(ownVariantValueText)
  }, [ownVariantValueText])

  const tracking = (variantValue?: string | null) => {
    if (!variantValue || !ownVariantValueText) return

    ProductDigitalData.pushSelectedVariant('button_click', variantValue, ownVariantValueText)
  }
  return (
    <>
      <h2 className="c-product-variant__heading">
        <strong>{variantName}:</strong> {selectedColor || ''}
      </h2>
      <div className="c-product-variant__list-container">
        {variantValues.map((productVariantValue: ProductVariantValue, i: number) => {
          if (!productVariantValue) {
            return
          }

          const isNotAvaialbleForSale = isNotAvailableForSale(productVariantValue)

          const hasNoAvailableVariants =
            mainProduct.availability?.isAvailableForSale &&
            (isNotAvaialbleForSale || hasNoValidVariantCombinations(productVariantValue))

          const imageSrc = getRelativeProductImageUrl(
            productVariantValue.product?.primaryImageId,
            60,
            60,
            mainProduct.fullDisplayName
          )
          const isSelected = productVariantValue.productErpIdentifier === mainProductErpIdentifier
          return (
            <React.Fragment key={`${productVariantValue.itemValueText}` + i}>
              <div
                onClick={() =>
                  tracking(`${variantNameEng} ${productVariantValue.itemValueText}`.trim())
                }>
                {hasNoAvailableVariants ? (
                  <a
                    onClick={() => handleOnUnavailableClicked(productVariantValue)}
                    className={classNames('c-product-variant--w-img', {
                      'c-product-variant--selected': isSelected,
                      'u-disabled': isNotAvaialbleForSale,
                    })}>
                    <>
                      {hasNoAvailableVariants && (
                        <span className="c-product-variant--w-img__asterisk">*</span>
                      )}
                      <Image
                        aria-label={ownVariantValueText}
                        width={60}
                        height={60}
                        priority={true}
                        src={imageSrc}
                        alt={productVariantValue.product?.nameSlug || ''}
                      />
                    </>
                  </a>
                ) : (
                  <Link
                    shallow
                    aria-label={ownVariantValueText}
                    href={getProductUrl(
                      productVariantValue.productErpIdentifier || '',
                      productVariantValue.product?.nameSlug || ''
                    )}
                    scroll={false}
                    className={classNames('c-product-variant--w-img', {
                      'c-product-variant--selected': isSelected,
                      'c-product-variant--img--not-available': hasNoAvailableVariants,
                      'u-disabled': isNotAvaialbleForSale,
                    })}>
                    <Image
                      width={60}
                      height={60}
                      priority={true}
                      src={imageSrc}
                      alt={productVariantValue.product?.nameSlug || ''}
                    />
                  </Link>
                )}
              </div>
            </React.Fragment>
          )
        })}
      </div>
    </>
  )
}

export default ProductVariantImageCards
